import { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { SOLICITUD_ALTERNATIVAS_API } from '../../../shared/services/solicitudAlternativas';
import InfoField from '../InfoField';
import InputSearch from '../InputSearch';
import ActionButton from '../ActionButton';
import { formatUsuarioDisplay } from '../../utils';

const NuevaSolicitudModal = ({ onClose, handleNewSolicitudCreated }) => {
    const tipoIncidencias = useSelector((state) => state.tipo_incidencias);
    const [search, setSearch] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [venta, setVenta] = useState(null);
    const [selectedPiezaIds, setSelectedPiezaIds] = useState([]);
    const [selectedMotivo, setSelectedMotivo] = useState('');
    const [comentario, setComentario] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const togglePieza = (piezaId) => {
        setSelectedPiezaIds((prev) =>
            prev.includes(piezaId)
                ? prev.filter((id) => id !== piezaId)
                : [...prev, piezaId],
        );
    };

    const handleSelectAllPiezas = () => {
        const allIds = venta?.piezas?.map((p) => p.id) ?? [];
        setSelectedPiezaIds(
            selectedPiezaIds.length === allIds.length ? [] : allIds,
        );
    };

    const handleSearch = async () => {
        if (!search || search.trim().length < 2) return;
        setIsLoading(true);
        setVenta(null);
        setSelectedPiezaIds([]);
        setSelectedMotivo('');
        setComentario('');
        try {
            const res = await SOLICITUD_ALTERNATIVAS_API.getVentaOrigen(
                search.trim(),
            );
            if (res?.success) {
                setVenta(res.data);
                setSearch('');
                if (res.data?.piezas?.length === 1) {
                    setSelectedPiezaIds([res.data.piezas[0].id]);
                }
            } else {
                toast.error(
                    'No se ha encontrado ninguna venta con ese presupuesto.',
                );
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.message ||
                e?.message ||
                'Error al buscar la venta.',
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!selectedPiezaIds.length) {
            toast.error('Selecciona al menos una pieza.');
            return;
        }
        if (!selectedMotivo) {
            toast.error('Selecciona un motivo.');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                pieza_ids: selectedPiezaIds,
                origen_id: venta.presupuesto_id,
                motivo_id: selectedMotivo,
                comentario: comentario.trim() || null,
            };
            const res = await SOLICITUD_ALTERNATIVAS_API.create(payload);
            if (res?.success) {
                toast.success('Solicitud creada correctamente.');
                handleNewSolicitudCreated(res.data);
                onClose();
            } else {
                toast.error(
                    res?.message || 'No se ha podido crear la solicitud.',
                );
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.message ||
                e?.message ||
                'Error al crear la solicitud.',
            );
        } finally {
            setSubmitting(false);
        }
    };

    const allPiezasSelected =
        venta?.piezas?.length > 0 &&
        selectedPiezaIds.length === venta.piezas.length;

    return (
        <div>
            <InputSearch
                value={search}
                setSearch={setSearch}
                handleSearch={handleSearch}
                isLoading={isLoading}
            />

            {venta && !isLoading && (
                <div className="mt-3">
                    <div
                        className="card p-3 mb-3"
                        style={{ backgroundColor: '#f8f9fa' }}
                    >
                        <h6
                            className="fw-bold text-uppercase mb-2"
                            style={{ color: '#215732' }}
                        >
                            Datos de la venta
                        </h6>

                        <div className="row g-3">
                            <div className="col-md-3">
                                <InfoField label="Presupuesto" value={venta.numero_presupuesto} />
                            </div>
                            <div className="col-md-3">
                                <InfoField
                                    label="Recambista"
                                    value={formatUsuarioDisplay(venta.recambista)}
                                />
                            </div>
                            <div className="col-md-6">
                                <InfoField label="Cliente" className="d-flex flex-column gap-1"
                                    value={
                                        <>
                                            <span>
                                                {venta?.cliente || '-'}
                                            </span>
                                            <span>
                                                {venta?.cliente_tlf || '-'}
                                            </span>
                                        </>
                                    } />
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <label
                            className="fw-bold text-uppercase mb-0"
                            style={{ color: '#215732' }}
                        >
                            Piezas
                        </label>
                        {venta.piezas?.length > 1 && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                style={{ color: '#215732' }}
                                onClick={handleSelectAllPiezas}
                            >
                                {allPiezasSelected
                                    ? 'Desmarcar todas'
                                    : 'Seleccionar todas'}
                            </button>
                        )}
                    </div>
                    {venta.piezas?.map((pieza) => {
                        const isSelected = selectedPiezaIds.includes(pieza.id);
                        const checkboxId = `pieza-check-${pieza.id}`;

                        return (
                            <div
                                key={pieza.id}
                                className={`card mb-3 p-3 ${isSelected ? 'border-success' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => togglePieza(pieza.id)}
                            >
                                <div className="d-flex align-items-start">
                                    <div className="me-3 mt-1">
                                        <input
                                            id={checkboxId}
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={isSelected}
                                            onChange={() => togglePieza(pieza.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                                width: 18,
                                                height: 18,
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </div>
                                    <div className="w-100">
                                        <div className="row g-3 mb-2">
                                            <div className="col-md-4">
                                                <InfoField small label="Nombre" value={pieza.nombre} />
                                            </div>
                                            <div className="col-md-8">
                                                <InfoField small label="Desguace" value={pieza.proveedor} />
                                            </div>
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <InfoField small label="Referencia" value={pieza.referencia} />
                                            </div>
                                            <div className="col-md-2">
                                                <InfoField small label="Precio compra" value={`${pieza.precio_compra}€`} />
                                            </div>
                                            <div className="col-md-2">
                                                <InfoField small label="Precio venta" value={`${pieza.precio_venta}€`} />
                                            </div>
                                            <div className="col-md-4">
                                                <InfoField small label="Precio envío" value={`${pieza.precio_envio}€`} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    <div className="form-group mt-3">
                        <label
                            className="fw-bold text-uppercase"
                            style={{ color: '#215732' }}
                        >
                            Motivo
                        </label>
                        <select
                            className="form-control"
                            style={{
                                appearance: 'auto',
                                WebkitAppearance: 'auto',
                                MozAppearance: 'auto',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                            }}
                            value={selectedMotivo}
                            onChange={(e) =>
                                setSelectedMotivo(parseInt(e.target.value))
                            }
                        >
                            <option value="">Seleccionar motivo...</option>
                            {tipoIncidencias?.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {(t.nombre ?? '').toUpperCase()} - {t.descripcion}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group mt-3">
                        <label
                            className="fw-bold text-uppercase"
                            style={{ color: '#215732' }}
                        >
                            Comentario{' '}
                            <small
                                className="text-muted"
                                style={{ textTransform: 'none' }}
                            >
                                (opcional)
                            </small>
                        </label>
                        <textarea
                            className="form-control"
                            rows={3}
                            value={comentario}
                            onChange={(e) => setComentario(e.target.value)}
                        />
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                        <ActionButton
                            disabled={
                                submitting ||
                                !selectedPiezaIds.length ||
                                !selectedMotivo
                            }
                            loading={submitting}
                            label="Crear solicitud"
                            onClick={handleSubmit}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default NuevaSolicitudModal;
